"use client";

import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  ArrowLeftRight,
  ArrowUpDown,
  ChevronDown,
  CircleChevronRight,
  RotateCcw,
} from "lucide-react";
import { useForm, useWatch } from "react-hook-form";
import type { z } from "zod";

// import { validatePromoCode } from "@/features/home/api/validatePromoCode";
import { validatePromotionCodev2 } from "@repo/ui/api/validatePromotionCode";
import { CheckboxField, InputField, PromoCode, RadioGroupField } from "@repo/ui/components";
import { Button } from "@repo/ui/components/ui/button";
import { Form } from "@repo/ui/components/ui/form";
import { zoneUrl } from "@repo/ui/constants/bookingBase";
import type { FareCalendarMap } from "@repo/ui/constants/flightAvailability";
import { usePageContext } from "@repo/ui/contexts/PageContext";
import type { CountryCitySelectorField as CountryCitySelectorFieldComponent } from "@repo/ui/FormFields/CountryCitySelectorField/CountryCitySelectorField";
import RangeDatePicker from "@repo/ui/FormFields/RangeDatePicker";
import { useFilteredStations } from "@repo/ui/Hooks/useFilteredStation";
import { getTabSessionId } from "@repo/ui/lib/tabSessionId";
import { capitalizeFirstLetter, cn } from "@repo/ui/lib/utils";
import type { HomePageContent } from "@repo/ui/types";
import type { City, GroupedStations } from "@repo/ui/types/stations";
import { getBookingOrigin } from "@repo/ui/utils/bookingOrigin";
import { ENVIRONMENT } from "@repo/ui/utils/environment";
import { fetchFareCalendar } from "@repo/ui/utils/fetchFares";
import { transformStationsWithMacCodes } from "@repo/ui/utils/transformStations";

import { useAppDispatch, useAppSelector } from "@repo/store/hooks";
import {
  selectCurrency,
  selectLocale,
  setCorporateSearch,
  setCorporateTravelDates,
  setDeparture,
  setDestination,
  setIsCheckIn,
  setIsRoundtrip,
  setMiles,
  setPassengersCount,
  setPromoCode,
  useCorporateSearch,
  useIsUserStaff,
} from "@repo/store/slices";
import { resetCheckinFlow } from "@repo/store/slices/checkinSlice";

// import { validatePromoCode } from "@/features/home/api/validatePromoCode";
import type { NormalizedHomePageSections } from "@/features/home/helpers/normalizeHomePageSections";
import parseCityAndCode from "@/features/home/helpers/parseCityAndCode";
import { MAIN_SEARCH_FLIGHT_TEST_IDS } from "@/test-ids";
import { GuestSelectorPopover } from "./GuestSelectorPopover";
import { useFlightFormUIState } from "./hooks/useFlightFormUIState";
import { defaultValues, searchFormSchema } from "./searchFormSchema";

// Loaded lazily so react-window and the heavy station sort/filter engine are
// fetched only when an origin/destination panel is actually shown, keeping them
// out of the initial search-widget hydration (see RangeCalendarPanel for the
// same pattern applied to the calendar).
const CountryCitySelectorField = lazy(() =>
  import("@repo/ui/FormFields/CountryCitySelectorField/CountryCitySelectorField").then((m) => ({
    default: m.CountryCitySelectorField,
  })),
) as unknown as typeof CountryCitySelectorFieldComponent;

const CountryCitySelectorFallback = () => (
  <div className="w-full rounded-b-lg border border-t-0 border-gray-200 bg-white p-4">
    <div className="animate-pulse space-y-2">
      <div className="h-6 rounded bg-gray-200" />
      <div className="h-6 rounded bg-gray-200" />
      <div className="h-6 rounded bg-gray-200" />
    </div>
  </div>
);

type FormData = z.infer<typeof searchFormSchema>;

type FlightsWidgetProps = {
  destinations: GroupedStations;
  setIsLoading: (loading: boolean) => void;
};

type CalendarState = {
  outbound: FareCalendarMap;
  inbound?: FareCalendarMap;
};

/**
 * Safely parse a "YYYY-MM-DD" date string (or Date object) into a local-time Date.
 *
 * WHY NOT `new Date(str)`?
 * When given a bare "YYYY-MM-DD" string, the JS spec treats it as UTC midnight.
 * In a timezone like Santiago (UTC-3 / UTC-4), UTC midnight becomes the
 * *previous evening* locally, so the displayed date is 1 day earlier than expected.
 *
 * This function uses `new Date(year, month-1, day)` which is always local time.
 */
const parseLocalDate = (dateInput: string | Date | null | undefined): Date | undefined => {
  if (!dateInput) return undefined;
  if (dateInput instanceof Date) {
    // Already a Date — strip the time portion to keep it local-midnight.
    return new Date(dateInput.getFullYear(), dateInput.getMonth(), dateInput.getDate());
  }
  if (typeof dateInput === "string") {
    // Strip any time / timezone suffix → keep only "YYYY-MM-DD"
    const cleanStr = dateInput.split("T")[0];
    if (cleanStr) {
      const [year, month, day] = cleanStr.split("-").map(Number);
      if (
        year !== undefined &&
        month !== undefined &&
        day !== undefined &&
        !isNaN(year) &&
        !isNaN(month) &&
        !isNaN(day)
      ) {
        // Local-time constructor — unaffected by the browser timezone offset.
        return new Date(year, month - 1, day);
      }
    }
  }
  return undefined;
};

export default function FlightsWidget({
  destinations,
  setIsLoading,
}: Readonly<FlightsWidgetProps>) {
  // ⚙️ CMS content
  const { normalizedSections } = usePageContext<HomePageContent, NormalizedHomePageSections>();
  const {
    commonInfo,
    defaultDeparture,
    defaultDestination,
    showFareCache,
    defaultNoOfMonths,
    noOfMonthsIfNoFareCache,
    defaultPromoCode,
    widgets,
  } = normalizedSections;

  const flightWidgetContent = widgets?.tabs
    ?.find((tab) => tab.id === "searchSmart")
    ?.content?.find((widget) => widget.type === "smartsearch_flight");

  const dispatch = useAppDispatch();
  const selectedCurrency = useAppSelector(selectCurrency);
  const selectedLocale = useAppSelector(selectLocale);
  const cultureCode = selectedLocale?.split("-")[1] ?? "CL"; // Default to CL if parsing fails
  const userAuth = useAppSelector((state) => state.auth.user);
  const isStaff = useIsUserStaff();
  const corporateSearch = useCorporateSearch();

  const transformedDestinations = useMemo(
    () => transformStationsWithMacCodes(destinations),
    [destinations],
  );

  useEffect(() => {
    setIsCheckIn(false);
  }, []);

  const [loadedMonths, setLoadedMonths] = useState<Set<string>>(new Set());
  const [calendarFares, setCalendarFares] = useState<CalendarState>({
    outbound: {},
    inbound: {},
  });
  const [loadingFares, setLoadingFares] = useState(false);
  const [departureCityCode, setDepartureCityCode] = useState<string | undefined>();
  const [destinationCityCode, setDestinationCityCode] = useState<string | undefined>();

  // Ref for the search box container to enable auto-scroll
  const searchBoxRef = useRef<HTMLDivElement>(null);

  // Auto-scroll function to position search box at top while keeping navigation visible
  const scrollToSearchBox = useCallback(() => {
    if (searchBoxRef.current) {
      const navHeight = 240; // Approximate height of navigation (adjust based on your navbar)
      const searchBoxTop = searchBoxRef.current.getBoundingClientRect().top + window.scrollY;
      const scrollPosition = searchBoxTop - navHeight;

      window.scrollTo({
        top: scrollPosition,
        behavior: "smooth",
      });
    }
  }, []);

  const getMonthKey = (date: Date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-01`;
  };

  const form = useForm<FormData>({
    resolver: zodResolver(searchFormSchema),
    defaultValues,
    mode: "onSubmit",
  });

  const {
    refs: {
      departureInputRef,
      destinationInputRef,
      departureDivRef,
      destinationDivRef,
      departureDateRef,
    },
    ui: {
      showDepartureSelector,
      setShowDepartureSelector,
      showDestinationSelector,
      setShowDestinationSelector,
      isSearchingDeparture,
      setIsSearchingDeparture,
      departureDateOpen,
      setDepartureDateOpen,
      showPromoCode,
      setShowPromoCode,
      showGuestSelector,
      setShowGuestSelector,
      isSearchingDestination,
      setIsSearchingDestination,
    },
    swapCities,
  } = useFlightFormUIState(form);

  const departure = useWatch({ control: form.control, name: "departure" });
  const destination = useWatch({ control: form.control, name: "destination" });
  const isRoundTrip = form.watch("tripType") === "roundTrip";

  useEffect(() => {
    dispatch(setIsRoundtrip(isRoundTrip));
  }, [dispatch, isRoundTrip]);

  const handleFetchFareCalendar = useCallback(
    async (depCode: string, destCode: string, tripType: string) => {
      try {
        const tabSessionId = getTabSessionId();
        const fareCalendar = await fetchFareCalendar(
          depCode,
          destCode,
          tripType,
          selectedCurrency,
          undefined,
          tabSessionId,
        );
        if (fareCalendar?.success) {
          setCalendarFares({
            outbound: fareCalendar.data?.outbound as FareCalendarMap,
            inbound: fareCalendar.data?.inbound as FareCalendarMap,
          });
        }
        setLoadingFares(false);
        return fareCalendar;
      } catch (error) {
        console.error("fetchFareCalendar error: ", { error });
        setLoadingFares(false);
      }
    },
    [selectedCurrency],
  );

  useEffect(() => {
    const { code: depCode } = parseCityAndCode(departure);
    const { code: destCode } = parseCityAndCode(destination);
    const tripType = isRoundTrip ? "RT" : "OW";

    if (!depCode || !destCode) return;

    setLoadingFares(true);
    setLoadedMonths(new Set());

    handleFetchFareCalendar(depCode, destCode, tripType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departure, destination, isRoundTrip, selectedCurrency]);

  useEffect(() => {
    if (!isRoundTrip) form.setValue("travelDates.to", null);
  }, [form, isRoundTrip]);

  const fetchFaresForMonth = useCallback(
    async (date: Date) => {
      const { code: depCode } = parseCityAndCode(departure);
      const { code: destCode } = parseCityAndCode(destination);
      if (!depCode || !destCode) return;

      const monthKey = getMonthKey(date);
      if (loadedMonths.has(monthKey)) return;

      setLoadingFares(true);
      // setFareError(null);

      try {
        const tripType = isRoundTrip ? "RT" : "OW";
        const tabSessionId = getTabSessionId();
        const res = await fetchFareCalendar(
          depCode,
          destCode,
          tripType,
          selectedCurrency,
          monthKey,
          tabSessionId,
        );

        if (res.success && res.data) {
          setCalendarFares((prev) => ({
            outbound: { ...prev.outbound, ...res.data!.outbound },
            inbound: { ...prev.inbound, ...res.data!.inbound },
          }));
          setLoadedMonths((prev) => new Set(prev).add(monthKey));
        } else {
          // setFareError(res.error ?? "Unknown error");
        }
      } catch (err: unknown) {
        console.error((err as Error)?.message || "Unknown error");
        // setFareError(err.message);
      } finally {
        setLoadingFares(false);
      }
    },
    [departure, destination, isRoundTrip, loadedMonths, selectedCurrency],
  );

  const handleResumeSearch = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    try {
      const raw = window.sessionStorage.getItem("flightSearchData");
      if (!raw) return;

      const data = JSON.parse(raw);

      // When restoring:
      form.setValue("tripType", data.tripType ?? "roundTrip");
      form.setValue("departure", data.departure ?? "");
      form.setValue("destination", data.destination ?? "");
      form.setValue("passengers", data.passengers);
      form.setValue("useAdvantageChecked", !!data.useAdvantageChecked);
      form.setValue("promoCode", data.promoCode ?? "");

      const { code: depStationCode } = parseCityAndCode(data.departure);
      const { code: destStationCode } = parseCityAndCode(data.destination);

      const depCityCode = findCityCode(depStationCode);
      const destCityCode = findCityCode(destStationCode);

      setDepartureCityCode(depCityCode);
      setDestinationCityCode(destCityCode);

      // Convert date strings to local-time Date objects.
      // IMPORTANT: do NOT use `new Date(str)` for "YYYY-MM-DD" strings — the JS
      // spec treats those as UTC midnight, which shifts the date 1 day back in
      // any timezone behind UTC (e.g. Santiago UTC-3/UTC-4).
      const from = parseLocalDate(data.travelDates?.from) ?? new Date();
      const to = parseLocalDate(data.travelDates?.to);

      form.setValue("travelDates", { from, to });

      const { city: departureCity, code: departureCode } = parseCityAndCode(data.departure);
      const { city: destinationCity, code: destinationCode } = parseCityAndCode(data.destination);

      dispatch(
        setCorporateSearch({
          departure: departureCity,
          departureCode,
          departureCountryCode: findCountryCode(departureCode) ?? "",
          departureCityCode: depCityCode ?? "",
          destination: destinationCity,
          destinationCode,
          destinationCountryCode: findCountryCode(destinationCode) ?? "",
          destinationCityCode: destCityCode ?? "",
          passengers: data.passengers,
          travelDates: data.travelDates,
          useAAdvantageMiles: !!data.useAdvantageChecked,
          promoCode: data.promoCode ?? "",
          isRoundtrip: data.tripType === "roundTrip",
          inboundSubtotal: 0,
          outboundSubtotal: 0,
        }),
      );
    } catch (err) {
      console.error("Failed to resume search:", err);
    }
  };

  useEffect(() => {
    if (corporateSearch.departureCode || corporateSearch.destinationCode) {
      form.reset({
        ...form.getValues(),
        tripType:
          corporateSearch.isRoundtrip !== undefined
            ? corporateSearch.isRoundtrip
              ? "roundTrip"
              : "oneWay"
            : corporateSearch.travelDates.to
              ? "roundTrip"
              : "oneWay",
        departure: corporateSearch.departure
          ? `${corporateSearch.departure} (${corporateSearch.departureCode})`
          : "",
        destination: corporateSearch.destination
          ? `${corporateSearch.destination} (${corporateSearch.destinationCode})`
          : "",
        passengers: corporateSearch.passengers,
        useAdvantageChecked: corporateSearch.useAAdvantageMiles,
        promoCode: corporateSearch.promoCode,
        travelDates: {
          from: parseLocalDate(corporateSearch.travelDates.from) ?? null,
          to: parseLocalDate(corporateSearch.travelDates.to) ?? null,
        },
      });

      if (corporateSearch.departureCityCode)
        setDepartureCityCode(corporateSearch.departureCityCode);
      if (corporateSearch.destinationCityCode)
        setDestinationCityCode(corporateSearch.destinationCityCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redux-persist rehydrates AFTER the component mounts, so the mount-effect above
  // often fires with an empty corporateSearch and skips the form reset.
  // This effect watches for corporateSearch.departureCode to become populated
  // (which happens when persist/REHYDRATE fires) and then does the form reset.
  // The `hasRestoredFromPersist` ref prevents it from firing again on every re-render.
  const hasRestoredFromPersist = useRef(false);
  useEffect(() => {
    if (hasRestoredFromPersist.current) return; // already ran once
    if (!corporateSearch.departureCode && !corporateSearch.destinationCode) return; // nothing to restore

    hasRestoredFromPersist.current = true;

    form.reset({
      ...form.getValues(),
      tripType:
        corporateSearch.isRoundtrip !== undefined
          ? corporateSearch.isRoundtrip
            ? "roundTrip"
            : "oneWay"
          : corporateSearch.travelDates.to
            ? "roundTrip"
            : "oneWay",
      departure: corporateSearch.departure
        ? `${corporateSearch.departure} (${corporateSearch.departureCode})`
        : "",
      destination: corporateSearch.destination
        ? `${corporateSearch.destination} (${corporateSearch.destinationCode})`
        : "",
      passengers: corporateSearch.passengers,
      useAdvantageChecked: corporateSearch.useAAdvantageMiles,
      promoCode: corporateSearch.promoCode,
      travelDates: {
        // parseLocalDate prevents the UTC-midnight 1-day shift for YYYY-MM-DD strings.
        from: parseLocalDate(corporateSearch.travelDates.from) ?? null,
        to: parseLocalDate(corporateSearch.travelDates.to) ?? null,
      },
    });

    if (corporateSearch.departureCityCode) setDepartureCityCode(corporateSearch.departureCityCode);
    if (corporateSearch.destinationCityCode)
      setDestinationCityCode(corporateSearch.destinationCityCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corporateSearch.departureCode, corporateSearch.destinationCode]);

  // When the browser restores this page from the Back/Forward Cache (bfcache),
  // the component is NOT remounted — all hooks and state are frozen and thawed.
  // This means useEffect([]) never re-fires, and any stale/wrong form values
  // from before navigation stay on screen. The `pageshow` event with `persisted=true`
  // signals a bfcache restore; we use it to re-populate the form from
  // sessionStorage `flightSearchData` (the authoritative source of what the user
  // actually searched — always clean YYYY-MM-DD strings).
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return; // Normal page load, not bfcache

      // Reset the flag so PostRehydrate can re-run if corporateSearch changes.
      hasRestoredFromPersist.current = false;

      // Re-populate from sessionStorage which has the original search dates
      // (written by onSubmit using formatLocalDate — always YYYY-MM-DD).
      try {
        const raw = window.sessionStorage.getItem("flightSearchData");
        if (!raw) return;
        const data = JSON.parse(raw) as {
          tripType?: string;
          departure?: string;
          destination?: string;
          passengers?: FormData["passengers"];
          useAdvantageChecked?: boolean;
          promoCode?: string;
          travelDates?: { from?: string; to?: string };
        };

        const from = parseLocalDate(data.travelDates?.from) ?? null;
        const to = parseLocalDate(data.travelDates?.to) ?? null;

        form.setValue("travelDates", { from: from ?? undefined, to: to ?? undefined });
      } catch {
        // If sessionStorage read fails, leave form as-is
      }
    };

    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleShowPromoCode = useCallback(
    (value: boolean) => {
      setShowPromoCode(value);
    },
    [setShowPromoCode],
  );

  const findCityCode = useCallback(
    (stationCode: string) => {
      for (const country in transformedDestinations) {
        const cityObj = transformedDestinations[country]?.find((s) => s.code === stationCode);
        if (cityObj) return cityObj.cityCode;
      }
      return undefined;
    },
    [transformedDestinations],
  );

  const findCountryCode = useCallback(
    (stationCode: string) => {
      for (const country in transformedDestinations) {
        const cityObj = transformedDestinations[country]?.find((s) => s.code === stationCode);
        if (cityObj) return cityObj.countryCode;
      }
      return undefined;
    },
    [transformedDestinations],
  );

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    if (data.useAdvantageChecked) {
      const url =
        ENVIRONMENT === "prod"
          ? "https://login.aa.com/login?flowId=uVb3JqyGyP&qmUser=undefined&qmSession=undefined&locale=es_CL"
          : "https://nonprod-login.aa.com/login?flowId=9v1a2q8xlW&qmUser=undefined&qmSession=undefined&locale=es_CL";

      window.location.assign(url);
      return;
    }

    dispatch(resetCheckinFlow());
    const formatLocalDate = (date: Date) =>
      `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

    const { city: departureCity, code: departureCode } = parseCityAndCode(data.departure);
    const { city: destinationCity, code: destinationCode } = parseCityAndCode(data.destination);

    const formattedData = {
      departure: departureCity,
      departureCode,
      departureCountryCode: findCountryCode(departureCode) ?? "",
      departureCityCode: departureCityCode ?? "",
      destination: destinationCity,
      destinationCode,
      destinationCountryCode: findCountryCode(destinationCode) ?? "",
      destinationCityCode: destinationCityCode ?? "",
      passengers: data.passengers,
      travelDates: {
        from: data.travelDates.from ? formatLocalDate(data.travelDates.from) : undefined,
        to: data.travelDates.to ? formatLocalDate(data.travelDates.to) : undefined,
      },
      useAAdvantageMiles: data.useAdvantageChecked ?? false,
      promoCode: data.promoCode ?? "",
      isRoundtrip: data.tripType === "roundTrip",
      inboundSubtotal: 0,
      outboundSubtotal: 0,
    };
    dispatch(setCorporateSearch(formattedData));

    // Validate promo code if present
    let promoValidateInfo = "";
    if (data.promoCode && data.promoCode.trim() !== "") {
      try {
        const tabSessionId = getTabSessionId();
        const validationResult = await validatePromotionCodev2(
          data.promoCode,
          tabSessionId,
          // formattedData.travelDates.from || "",
          // formattedData.travelDates.to || "",
        );

        if (validationResult.data?.isValid) {
          promoValidateInfo = "PROMO_PENDING";
        }

        sessionStorage.setItem("promo_reloading", "true");
      } catch (error) {
        console.error("Promo code validation failed:", error);

        // Clear promo code from corporate state on error
        dispatch(setPromoCode(""));

        // On error, remove promo code and proceed
        formattedData.promoCode = "";
        data.promoCode = "";
      }
    }

    // Persist in session storage (existing behavior)
    window.sessionStorage.setItem(
      "flightSearchData",
      JSON.stringify({ ...data, travelDates: formattedData.travelDates }),
    );

    // Do not set corporate slice in the home app; hydration happens in booking subdomain
    // Route users by role: staff -> staff app, agencies -> agencies app, member -> member app, else anonymous app.
    const isAgency = userAuth?.organizationType === "travel_agencies";
    const isMember = userAuth?.userRole === "WWWM";

    // Keep compatibility if downstream types expose getBookingOrigin with only 2 params.
    const bookingOrigin =
      isAgency && !isStaff && !isMember
        ? process.env.NEXT_PUBLIC_AGENCIES_APP_URL || "http://localhost:3003"
        : getBookingOrigin(isStaff, isMember);

    // Fallback to current origin if booking origin is missing to avoid URL constructor errors
    const baseOrigin =
      bookingOrigin || (typeof window !== "undefined" ? window.location.origin : "");

    const targetPath = isStaff
      ? zoneUrl("staff", "/flight")
      : isAgency
        ? "/booking/agencies/flight"
        : isMember
          ? zoneUrl("member", "/flight")
          : zoneUrl("booking", "/flight");
    const url = new URL(targetPath, baseOrigin);

    // Append corporate details as compact query params
    url.searchParams.set("c_dep", formattedData.departure || "");
    url.searchParams.set("c_depCode", formattedData.departureCode || "");
    url.searchParams.set("c_depCountry", formattedData.departureCountryCode || "");
    url.searchParams.set("c_depCityCode", formattedData.departureCityCode || "");
    url.searchParams.set("c_des", formattedData.destination || "");
    url.searchParams.set("c_desCode", formattedData.destinationCode || "");
    url.searchParams.set("c_desCountry", formattedData.destinationCountryCode || "");
    url.searchParams.set("c_desCityCode", formattedData.destinationCityCode || "");

    if (userAuth) {
      url.searchParams.set("userRole", userAuth.userRole);
      url.searchParams.set("firstName", userAuth.firstName);
      url.searchParams.set("lastName", userAuth.lastName);
      if (userAuth.email) {
        url.searchParams.set("email", userAuth.email);
      }
    }

    if (formattedData.travelDates.from)
      url.searchParams.set("c_from", formattedData.travelDates.from);
    if (formattedData.travelDates.to) url.searchParams.set("c_to", formattedData.travelDates.to);
    if (formattedData.passengers?.adults !== undefined)
      url.searchParams.set("c_ad", String(formattedData.passengers.adults));
    if (formattedData.passengers?.teenagers !== undefined)
      url.searchParams.set("c_teen", String(formattedData.passengers.teenagers));
    if (formattedData.passengers?.children !== undefined)
      url.searchParams.set("c_ch", String(formattedData.passengers.children));
    if (formattedData.passengers?.infants !== undefined)
      url.searchParams.set("c_in", String(formattedData.passengers.infants));
    if (formattedData.useAAdvantageMiles) {
      url.searchParams.set("c_miles", "1");
    }
    if (formattedData.promoCode != "" && formattedData.promoCode) {
      url.searchParams.set("c_promo", formattedData.promoCode);
      url.searchParams.set("c_promoValidateInfo", promoValidateInfo || "PROMO_INVALID");
    }

    // Add tab session ID to preserve state across zone navigation
    if (typeof window !== "undefined") {
      const tabSessionId = getTabSessionId();
      if (tabSessionId) {
        url.searchParams.set("tsid", tabSessionId);
      }
    }

    // Add clearData flag to clear flight zone data when navigating from main zone
    url.searchParams.set("clearData", "true");

    if (typeof window !== "undefined") {
      window.location.assign(url.toString());
    }
  };

  const handleDepartureSelect = useCallback(
    (value: City) => {
      scrollToSearchBox();
      setIsSearchingDeparture(false);
      setShowDepartureSelector(false);
      setShowDestinationSelector(true);

      const cityCode = findCityCode(value.code);
      setDepartureCityCode(cityCode); // store locally

      dispatch(
        setDeparture({
          departure: value.city,
          departureCode: value.code,
          departureCountryCode: value.countryCode || "",
          departureCityCode: cityCode || "",
        }),
      );

      // Store as "City (CODE)" so we can reliably split into name/code later
      form.setValue("departure", `${value.city} (${value.code})`);
    },
    [
      scrollToSearchBox,
      setIsSearchingDeparture,
      setShowDepartureSelector,
      setShowDestinationSelector,
      findCityCode,
      dispatch,
      form,
    ],
  );

  const handleDestinationSelect = useCallback(
    (value: City) => {
      scrollToSearchBox();
      setIsSearchingDestination(false);
      setShowDestinationSelector(false);
      setDepartureDateOpen(true);
      departureDateRef.current?.focus();

      const cityCode = findCityCode(value.code);
      setDestinationCityCode(cityCode); // store locally

      dispatch(
        setDestination({
          destination: value.city,
          destinationCode: value.code,
          destinationCountryCode: value.countryCode || "",
          destinationCityCode: cityCode || "",
        }),
      );

      // Store as "City (CODE)" so we can reliably split into name/code later
      form.setValue("destination", `${value.city} (${value.code})`);
    },
    [
      scrollToSearchBox,
      setIsSearchingDestination,
      setShowDestinationSelector,
      setDepartureDateOpen,
      departureDateRef,
      findCityCode,
      dispatch,
      form,
    ],
  );

  const handleDateSelect = useCallback(
    (outbound?: Date, inbound?: Date) => {
      const formatLocalDate = (date: Date) =>
        `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

      dispatch(
        setCorporateTravelDates({
          from: outbound ? formatLocalDate(outbound) : undefined,
          to: inbound ? formatLocalDate(inbound) : undefined,
        }),
      );

      if (isRoundTrip && outbound && !inbound) {
        setDepartureDateOpen(false);
        setDepartureDateOpen(true);
      } else {
        setShowGuestSelector(true);
        setDepartureDateOpen(false);
      }
    },
    [isRoundTrip, setDepartureDateOpen, setShowGuestSelector, dispatch],
  );

  const handleFieldIconCallback = useCallback(() => {
    handleShowPromoCode(false);
    form.setValue("promoCode", "");
  }, [form, handleShowPromoCode]);

  const departureValue = form.watch("departure");
  const destinationValue = form.watch("destination");
  const promoCode = form.watch("promoCode");

  const { filteredDepartures, filteredDestinations } = useFilteredStations(
    transformedDestinations,
    departureValue,
    destinationValue,
  );

  useEffect(() => {
    if (defaultPromoCode) form.setValue("promoCode", defaultPromoCode);
  }, [form, defaultPromoCode]);

  const passengersValue = form.watch("passengers");
  const useAdvantageCheckedValue = form.watch("useAdvantageChecked");

  useEffect(() => {
    dispatch(setPassengersCount(passengersValue));
  }, [dispatch, passengersValue]);

  useEffect(() => {
    dispatch(setPromoCode(promoCode ?? ""));
  }, [dispatch, promoCode]);

  useEffect(() => {
    dispatch(setMiles(!!useAdvantageCheckedValue));
  }, [dispatch, useAdvantageCheckedValue]);

  // const buildDataForTealiumSearchEvent = async (data: FormattedData) => {
  //   const searchContext: SearchContext = {
  //     origin_iata: data.departureCode,
  //     destination_iata: data.destinationCode,
  //     market: "DOM",
  //     departure_journey_date: data.travelDates.from || "",
  //     return_journey_date: data.travelDates.to || "",
  //     trip_type: isRoundTrip ? "R" : "O",
  //     purchase_anticipation: 0,
  //     pax_adult_count: data.passengers.adults,
  //     pax_children_count: data.passengers.children,
  //     pax_infant_count: data.passengers.infants,
  //     promocode: data.promoCode || "",
  //   };
  //   const role_code = userAuth?.userRole || "Anonymous";
  //   const eventData = await searchClicked(searchContext, { role_code });
  //   pushTealiumEvent(eventData, "link");
  // };

  return (
    <div className="" ref={searchBoxRef}>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit, () => {})}
          data-testid={MAIN_SEARCH_FLIGHT_TEST_IDS.formContainer}
        >
          <div className="space-y-4 md:p-4">
            <div>
              <RadioGroupField
                control={form.control}
                name="tripType"
                variant="blue"
                options={[
                  {
                    label: capitalizeFirstLetter(commonInfo?.oneWayLabel),
                    value: "oneWay",
                    testId: "oneway-radio",
                  },
                  {
                    label: capitalizeFirstLetter(commonInfo?.roundTripLabel),
                    value: "roundTrip",
                    testId: "round-trip-radio",
                  },
                ]}
                orientation="horizontal"
              />
            </div>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-4">
              <div className="relative flex flex-col self-start w-full rounded md:flex-row">
                <div className="w-full">
                  <InputField
                    autoComplete="off"
                    control={form.control}
                    name="departure"
                    placeholder={commonInfo.originLabel || "Origin"}
                    ref={departureInputRef}
                    onFocus={() => {
                      scrollToSearchBox();
                      setIsSearchingDeparture(false);
                      setShowDepartureSelector(true);
                      setShowDestinationSelector(false);
                    }}
                    onClick={() => {
                      scrollToSearchBox();
                      setIsSearchingDeparture(false);
                      setShowDepartureSelector(true);
                      setShowDestinationSelector(false);
                    }}
                    onKeyup={(val) => setIsSearchingDeparture(!!val)}
                    inputClassName="h-10 rounded-none rounded-t-md md:rounded-t-[0px] md:rounded-l-md w-full px-4"
                    className="flex-1"
                    icon={<ChevronDown className="w-4 h-4 text-gray-400" />}
                  />
                </div>
                <div className="absolute z-10 items-center justify-center hidden -translate-x-1/2 -translate-y-1/2 border rounded-full top-1/2 left-1/2 w-7 h-7 border-neutral-300 bg-neutral-50 md:flex">
                  <ArrowLeftRight size={16} onClick={swapCities} className="cursor-pointer" />
                </div>
                <div className="absolute z-10 flex items-center justify-center w-8 h-8 -translate-y-1/2 border rounded-full right-2 top-1/2 text-brand-gray border-neutral-300 bg-neutral-50 md:hidden">
                  <ArrowUpDown size={18} onClick={swapCities} className="cursor-pointer" />
                </div>
                <div className="w-full">
                  <InputField
                    autoComplete="off"
                    control={form.control}
                    name="destination"
                    placeholder={commonInfo.destinationLabel || "Destination"}
                    ref={destinationInputRef}
                    disabled={!departureValue}
                    onFocus={() => {
                      scrollToSearchBox();
                      setIsSearchingDestination(false);
                      setShowDestinationSelector(true);
                      setShowDepartureSelector(false);
                    }}
                    onClick={() => {
                      scrollToSearchBox();
                      setIsSearchingDestination(false);
                      setShowDestinationSelector(true);
                      setShowDepartureSelector(false);
                    }}
                    onKeyup={(val) => setIsSearchingDestination(!!val)}
                    inputClassName="h-10 rounded-none rounded-b-md md:rounded-l-[0px] md:rounded-r-md w-full px-4 border-t-0 md:border-t-1"
                    className="flex-1"
                    icon={<ChevronDown className="w-4 h-4 text-gray-400" />}
                  />
                </div>
              </div>
              <div
                className={cn(
                  "grid grid-cols-1 gap-2 md:gap-4",
                  isRoundTrip ? "md:grid-cols-3" : "md:grid-cols-2",
                )}
              >
                <div className={cn(isRoundTrip ? "md:col-span-2" : "md:col-span-1")}>
                  <RangeDatePicker
                    onMonthChange={fetchFaresForMonth}
                    control={form.control}
                    name="travelDates"
                    form={form}
                    placeholder={commonInfo.departureDateLabel || "Departure date"}
                    commonInfo={commonInfo}
                    isRequired
                    isRoundTrip={isRoundTrip}
                    isOpen={departureDateOpen}
                    setIsOpen={setDepartureDateOpen}
                    disabled={!departureValue && !destinationValue}
                    calendarFares={calendarFares}
                    handleDateSelect={handleDateSelect}
                    loadingFares={loadingFares}
                    returnDateLabel={commonInfo.returnDateLabel}
                    showFare={showFareCache}
                    noOfMonthsToShowFareCache={noOfMonthsIfNoFareCache || defaultNoOfMonths}
                    className="min-h-[40px]"
                    dateFormat="dd/MM/yyyy"
                    shortenDate={true}
                  />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-1 gap-2 md:gap-4">
                  <GuestSelectorPopover
                    control={form.control}
                    name="passengers"
                    disabled={
                      !departureValue || !destinationValue || !form.getValues("travelDates.from")
                    }
                    isGuestSelectorOpen={showGuestSelector}
                    setShowGuestSelector={setShowGuestSelector}
                    adultLabel={commonInfo.adultLabel}
                    teenagerLabel={commonInfo.teenagerLabel}
                    childLabel={commonInfo.childLabel}
                    infantLabel={commonInfo.infantLabel}
                    adultDescriptionLabel={commonInfo.adultDescriptionLabel}
                    teenagerDescriptionLabel={commonInfo.teenagerDescriptionLabel}
                    childDescriptionLabel={commonInfo.childDescriptionLabel}
                    infantDescriptionLabel={commonInfo.infantDescriptionLabel}
                    confirmLabel={commonInfo.confirmLabel}
                  />
                  <div className="md:hidden">
                    <PromoCode
                      control={form.control}
                      name="promoCode"
                      buttonLabel={flightWidgetContent?.promoCodeLabel ?? ""}
                      showPromoCode={showPromoCode}
                      promoCode={promoCode}
                      handleFieldIconCallback={handleFieldIconCallback}
                      handleShowPromoCode={handleShowPromoCode}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div
              className={cn(
                "relative transition-all duration-300 ease-in-out transform origin-top translate-y-0",
                showDepartureSelector
                  ? "opacity-100 scale-100 z-[50]"
                  : "hidden pointer-events-none z-[30]",
              )}
              ref={departureDivRef}
            >
              {showDepartureSelector && (
                <Suspense fallback={<CountryCitySelectorFallback />}>
                  <CountryCitySelectorField
                    control={form.control}
                    name="departure"
                    data={filteredDepartures}
                    searchValue={isSearchingDeparture ? departureValue : ""}
                    onSelect={handleDepartureSelect}
                    fallbackData={transformedDestinations}
                    allDestinationsLabel={flightWidgetContent?.allDestinationsLabel ?? ""}
                    citiesLabel={flightWidgetContent?.citiesLabel ?? ""}
                    defaultCountry={defaultDeparture}
                    cultureCode={cultureCode}
                  />
                </Suspense>
              )}
            </div>
            <div
              className={cn(
                "relative transition-all duration-300 ease-in-out transform origin-top translate-y-0",
                showDestinationSelector
                  ? "opacity-100 scale-100 z-[60]"
                  : "hidden pointer-events-none z-[40]",
              )}
              ref={destinationDivRef}
            >
              {showDestinationSelector && (
                <Suspense fallback={<CountryCitySelectorFallback />}>
                  <CountryCitySelectorField
                    control={form.control}
                    name="destination"
                    data={filteredDestinations}
                    searchValue={isSearchingDestination ? destinationValue : ""}
                    onSelect={handleDestinationSelect}
                    fallbackData={transformedDestinations}
                    allDestinationsLabel={flightWidgetContent?.allDestinationsLabel ?? ""}
                    citiesLabel={flightWidgetContent?.citiesLabel ?? ""}
                    defaultCountry={defaultDestination}
                    cultureCode={cultureCode}
                  />
                </Suspense>
              )}
            </div>
            <div>
              <CheckboxField
                control={form.control}
                name="useAdvantageChecked"
                label={flightWidgetContent?.aadvantageLabel ?? ""}
                className="flex items-center"
                testId="use-advantage"
              />
            </div>
            <div className="mt-0">
              <div className="flex flex-col items-center justify-between md:flex-row">
                <div>
                  <Button
                    type="button"
                    variant="ghost"
                    className="font-semibold underline transition-colors duration-300 ease-in-out border-white cursor-pointer group text-brand-blue hover:text-brand-cyan hover:border-brand-cyan border-1 hover:bg-transparent"
                    onClick={handleResumeSearch}
                  >
                    <RotateCcw className="group-hover:text-brand-red" />
                    {flightWidgetContent?.redoSearchLabel ?? ""}
                  </Button>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <div className="hidden md:block">
                    <PromoCode
                      control={form.control}
                      name="promoCode"
                      showPromoCode={showPromoCode}
                      promoCode={promoCode}
                      buttonLabel={flightWidgetContent?.promoCodeLabel ?? ""}
                      handleFieldIconCallback={handleFieldIconCallback}
                      handleShowPromoCode={handleShowPromoCode}
                    />
                  </div>
                  <Button
                    type="submit"
                    variant="destructive"
                    disabled={
                      !departureValue ||
                      !destinationValue ||
                      !form.getValues("travelDates.from") ||
                      !form.getValues("passengers")
                    }
                    data-testid="search-flights-btn"
                  >
                    {flightWidgetContent?.buttonText ?? ""}
                    <CircleChevronRight className="w-4 h-4 -ml-1" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
